import React, { useEffect, useState, useCallback, useRef } from 'react';
import 'react-calendar/dist/Calendar.css';
import createSchedule from "../../../../services/schedule/createSchedule";
import { message, DatePicker, Modal, Button, Input, AutoComplete, Spin, Row, Col } from 'antd';
import dayjs from 'dayjs';
import { AiOutlineSchedule } from "react-icons/ai";
import AtribuirResponsavel from 'components/AtribuirResponsavelSchedule';
import findProcesses from 'services/processes/findProcesses';
import listCollaborators from 'services/collaborator/listCollaborators';
import { getErrorMessage } from 'utils/errorMessage';

const { TextArea } = Input;

function Agenda({ processN, open, onOpenChange, hideButton = false, onCreated }) {
  const [dateTime, setDateTime] = useState(dayjs());
  const [title, setTitle] = useState('');
  const [processNumber, setProcessNumber] = useState(processN);
  
  // Novas states para as partes do processo
  const [parteAutora, setParteAutora] = useState('');
  const [parteContraria, setParteContraria] = useState('');

  const [description, setDescription] = useState('');
  const [showModal, setShowModal] = useState(false);
  // Este componente existe em cada linha da tabela: só busca dados com o modal aberto
  const isOpen = typeof open === 'boolean' ? open : showModal;
  const [selectedUsersChange, setSelectedUsersChange] = useState({});
  const [initialResponsibleNames, setInitialResponsibleNames] = useState('');
  const [initialResponsibleEmails, setInitialResponsibleEmails] = useState('');
  const [collaborators, setCollaborators] = useState([]);
  const lastAutoProcessRef = useRef('');
  const [processSearch, setProcessSearch] = useState('');
  const [processOptions, setProcessOptions] = useState([]);
  const [processLoading, setProcessLoading] = useState(false);

  const addEvent = async () => {
    if (!title?.trim()) {
      message.error('Título é obrigatório');
      return;
    }
    if (!processNumber?.trim()) {
      message.error('Número do processo é obrigatório');
      return;
    }
    if (!dateTime) {
      message.error('Data e hora são obrigatórios');
      return;
    }

    const eventDate = dateTime.toDate();
    const formattedHour = dateTime.format('HH:mm');

    const newEvent = {
      title,
      date: eventDate,
      processNumber,
      parteAutora,     // Enviando novos campos
      parteContraria,  // Enviando novos campos
      description,
      hour: formattedHour,
      color: randomColor(),
      responsavelEmails: selectedUsersChange.emails,
      responsavelNames: selectedUsersChange.names,
      cronTime: convertToCronDate(eventDate),
    };

    createSchedule(newEvent)
      .then(() => {
        message.success(`Evento ${newEvent.title} Criado com sucesso`);
        if (typeof onOpenChange === 'function') {
          onOpenChange(false);
        } else {
          setShowModal(false);
        }
        if (typeof onCreated === 'function') {
          onCreated(newEvent);
        }
      })
      .catch(err => {
        message.error(getErrorMessage(err, 'Não foi possível agendar o compromisso.'));
      });
  };

  const convertToCronDate = (date) => {
    const oneDay = 24 * 60 * 60 * 1000;
    const previousDate = new Date(date.getTime() - oneDay);
    const minute = previousDate.getMinutes();
    const hour = previousDate.getHours();
    const dayOfMonth = previousDate.getDate();
    const month = previousDate.getMonth() + 1;
    const dayOfWeek = previousDate.getDay();
    return `${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`;
  };

  const randomColor = () => {
    return '#' + Math.floor(Math.random() * 16777215).toString(16);
  };

  useEffect(() => {
    if (processN !== undefined) {
      setProcessNumber(processN || '');
    }
  }, [processN]);

  useEffect(() => {
    setProcessSearch(processNumber || '');
  }, [processNumber]);

  const fetchCollaborators = useCallback(() => {
    listCollaborators()
      .then(({ data }) => {
        setCollaborators((data || []).filter(u => u.email !== 'admin@gmail.com'));
      })
      .catch(() => {
        setCollaborators([]);
      });
  }, []);

  useEffect(() => {
    if (isOpen) fetchCollaborators();
  }, [isOpen, fetchCollaborators]);

  const clearResponsibleSelection = useCallback(() => {
    setInitialResponsibleNames('');
    setInitialResponsibleEmails('');
    setSelectedUsersChange({});
    setParteAutora('');      // Limpa polo positivo
    setParteContraria('');   // Limpa polo negativo
  }, []);

  // Função estendida para auto-preencher os dados do processo
  const applyProcessData = useCallback((processData) => {
    if (!processData) {
      clearResponsibleSelection();
      return;
    }

    // Autofill dos polos baseado no retorno da API
    setParteAutora(processData?.PartePROPrincipalNome || processData?.Cliente || processData?.nomeCliente || '');
    setParteContraria(processData?.ParteContraria || processData?.ParteAdversa || processData?.Reu || '');

    let names = [];
    let emails = [];

    if (Array.isArray(processData.responsaveis) && processData.responsaveis.length) {
      processData.responsaveis.forEach((r) => {
        const fullName = `${r.first_name || ''} ${r.last_name || ''}`.trim();
        if (fullName) names.push(fullName);
        if (r.email) emails.push(r.email);
      });
    }

    if ((!emails.length || !names.length) && (processData.ResponsavelId || processData.ResponsavelNome)) {
      const ids = String(processData.ResponsavelId || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      const namesFromProcess = String(processData.ResponsavelNome || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      if (ids.length) {
        ids.forEach((id) => {
          const found = collaborators.find(c => String(c.id) === String(id) || String(c.id_user) === String(id));
          if (found?.email) emails.push(found.email);
          const fullName = `${found?.first_name || ''} ${found?.last_name || ''}`.trim();
          if (fullName) names.push(fullName);
        });
      }

      if (!names.length && namesFromProcess.length) {
        names = namesFromProcess;
      }
    }

    const uniqueNames = Array.from(new Set(names)).filter(Boolean);
    const uniqueEmails = Array.from(new Set(emails)).filter(Boolean);

    if (!uniqueEmails.length) {
      setInitialResponsibleNames('');
      setInitialResponsibleEmails('');
      setSelectedUsersChange({});
      return;
    }

    const namesValue = uniqueNames.join(';');
    const emailsValue = uniqueEmails.join(';');
    setInitialResponsibleNames(namesValue);
    setInitialResponsibleEmails(emailsValue);
    setSelectedUsersChange({ names: namesValue, emails: emailsValue });
  }, [collaborators, clearResponsibleSelection]);

  const normalizeProcessNumber = useCallback((value) => {
    return String(value || '').replace(/\D/g, '');
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const query = String(processNumber || '').trim();
    if (query.length < 2) return;

    const normalizedQuery = normalizeProcessNumber(query);
    if (!normalizedQuery) return;

    if (lastAutoProcessRef.current === normalizedQuery && selectedUsersChange?.emails) return;

    const handle = setTimeout(async () => {
      try {
        const { data } = await findProcesses(undefined, query);
        const processes = data?.process || [];
        const match = processes.find((p) => {
          const numero = p?.NProcesso || p?.nProcesso || p?.numeroProcesso || p?.Processo || '';
          return normalizeProcessNumber(numero) === normalizedQuery;
        }) || processes[0];

        if (match) {
          lastAutoProcessRef.current = normalizedQuery;
          applyProcessData(match);
        }
      } catch {
        // ignore
      }
    }, 350);

    return () => clearTimeout(handle);
  }, [isOpen, processNumber, normalizeProcessNumber, applyProcessData, selectedUsersChange]);

  useEffect(() => {
    const query = processSearch.trim();
    if (!isOpen || query.length < 2) {
      setProcessOptions([]);
      setProcessLoading(false);
      return;
    }

    setProcessLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await findProcesses(undefined, query);
        const processes = data?.process || [];
        const options = processes
          .map(p => {
            const numero = p?.NProcesso || p?.nProcesso || p?.numeroProcesso || p?.Processo || '';
            if (!numero) return null;
            const cliente = p?.PartePROPrincipalNome || p?.Cliente || p?.nomeCliente || '';
            return {
              value: numero,
              label: cliente ? `${numero} — ${cliente}` : numero,
              process: p
            };
          })
          .filter(Boolean)
          .slice(0, 10);
        setProcessOptions(options);
      } catch (error) {
        setProcessOptions([]);
      } finally {
        setProcessLoading(false);
      }
    }, 350);

    return () => clearTimeout(handle);
  }, [isOpen, processSearch]);

  const handleOpenChange = (value) => {
    if (typeof onOpenChange === 'function') {
      onOpenChange(value);
    } else {
      setShowModal(value);
    }
  };

  return (
    <>
      <Modal
        title="Adicionar Evento"
        open={isOpen}
        onOk={addEvent}
        onCancel={() => handleOpenChange(false)}
        width={600} // Aumentado um pouco para acomodar melhor os campos lado a lado
      >
        <Input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Título"
          className="modal-input"
          style={{ marginBottom: 10 }}
        />
        
        <AutoComplete
          value={processNumber}
          options={processOptions}
          onSearch={value => setProcessSearch(value)}
          onSelect={(value, option) => {
            setProcessNumber(value);
            setProcessSearch(value);
            applyProcessData(option?.process);
          }}
          onChange={(value) => {
            setProcessNumber(value);
            setProcessSearch(value);
            if (!value) clearResponsibleSelection();
          }}
          placeholder="Número do Processo"
          size="large"
          style={{ width: '100%', marginBottom: 10 }}
          filterOption={false}
          getPopupContainer={(triggerNode) => triggerNode.parentElement}
          notFoundContent={processLoading ? <Spin size="small" /> : null}
        >
          <Input
            type="text"
            className="modal-input"
          />
        </AutoComplete>

        {/* Campos de polos do processo inseridos lado a lado */}
        <Row gutter={10} style={{ marginBottom: 10 }}>
          <Col span={12}>
            <Input
              type="text"
              value={parteAutora}
              onChange={(e) => setParteAutora(e.target.value)}
              placeholder="Polo ativo"
              className="modal-input"
            />
          </Col>
          <Col span={12}>
            <Input
              type="text"
              value={parteContraria}
              onChange={(e) => setParteContraria(e.target.value)}
              placeholder="Polo passivo"
              className="modal-input"
            />
          </Col>
        </Row>

        <AtribuirResponsavel
          onSelectedUsersChange={setSelectedUsersChange}
          initialNames={initialResponsibleNames}
          initialEmails={initialResponsibleEmails}
        />
        
        <TextArea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descrição"
          className="modal-textarea"
          style={{ marginBottom: 10 }}
        />
        
        <DatePicker 
          value={dateTime}
          onChange={(val) => setDateTime(val)}
          showTime={{ format: 'HH:mm' }} 
          format="DD/MM/YYYY HH:mm"
          placeholder="Selecione Data e Hora"
          style={{ width: '100%', marginBottom: 10 }} 
          needConfirm
        />
      </Modal>
      {!hideButton && (
        <Button type="primary" title="Agendar compromisso" aria-label="Agendar compromisso" icon={<AiOutlineSchedule size={18} />} onClick={() => handleOpenChange(true)} />
      )}
    </>
  );
}

export default Agenda;
